import { Button, Checkbox, Col, Divider, Form, Input, Modal, Row, Select, Slider, TimePicker, Typography } from "antd";
import { useReactDraggable } from "../../../hooks/useReactDraggable";
import { usePayAndPark } from "./index.hook";
import Draggable from "react-draggable";
import styles from "./index.module.less";
import { formItemLayout, inputLayout2 } from "../../../constants/ApplicationConstants/FormConstants";
import Loading from "../../common/Loading";
import { General } from "../../../util/General";
import CircularProgressBar from "../../common/CircularProgressBar";
import FormInputRules from "../../../util/FormInputRules";
import KeyFobAllocation from "./KeyFobAllocation";
import moment from "moment";

type PayAndParkModalProps = {
  handleOnClose: () => void;
  handleRefreshList: () => void;
  selectedDate: string;
};

const PayAndParkModal = (props: PayAndParkModalProps) => {
  const config = usePayAndPark({ handleOnClose: props.handleOnClose, handleRefreshList: props.handleRefreshList, selectedDate: props.selectedDate });
  const { Text } = Typography;

  const { bounds, onStart } = useReactDraggable();

  const inputLayout = { wrapperCol: { ...inputLayout2["wrapperCol"], lg: 6 } };

  const checkSlotAVailability = () => {
    return (
      <>
        <h4 className={styles["section-header"]}>
          Check Slot Availability
          <span className={styles["slot-availability-header-span"]}>({moment(props.selectedDate).format("DD-MM-YYYY")})</span>
        </h4>

        <Form.Item initialValue={config.getFirstAvailableTime()} {...inputLayout} name="time" label="Time" required>
          <TimePicker showNow={false} minuteStep={30} format={config.timePickerFormat} disabledTime={config.disabledRangeTime} disabled={config.currentStep !== config.Steps.CHECK_AVAILABLE_SLOTS} />
        </Form.Item>

        <Form.Item initialValue={1} {...inputLayout} name="hours" label="Duration (Hrs)" required>
          <Slider min={1} max={10} step={1} disabled={config.currentStep !== config.Steps.CHECK_AVAILABLE_SLOTS} />
        </Form.Item>

        <Form.Item {...inputLayout} name="vehicle_type_id" label="Vehicle Type" required rules={[FormInputRules.requiredFieldSelectRule("vehicle type")]}>
          <Select disabled={config.currentStep !== config.Steps.CHECK_AVAILABLE_SLOTS} showSearch listHeight={200} allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select Vehicle Type">
            {config.advanceBookingQuotaList.map((advanceBookingQuota) => (
              <Select.Option key={advanceBookingQuota?.vehicleType?.id} value={advanceBookingQuota?.vehicleType?.id}>
                {advanceBookingQuota?.vehicleType?.vehicle_type_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </>
    );
  };

  const raiseAdvanceBookingRequest = () => {
    if (!config.slotDetails) return <></>;
    return (
      <>
        <h4 className={styles["section-header"]}>
          Raise Advance Booking Request
          <span className={styles["raise-abr-header-span"]}>
            ({moment(config.slotDetails.fromDate).format("DD-MM-YYYY HH:mm")} to {moment(config.slotDetails.toDate).format("DD-MM-YYYY HH:mm")})
          </span>
        </h4>

        <div className={styles["raise-abr-container"]}>
          <Row gutter={200}>
            <Col span={12}>
              <Form.Item {...inputLayout2} name="mobile_number" label="Mobile Number" required>
                <div className={styles["raise-abr-verify-number-div"]}>
                  <Input name="mobile_number" prefix={"+91"} disabled={config.currentStep !== config.Steps.RAISE_ABR_REQUEST} />
                  <Button htmlType="button" className={`gx-mb-0 button-gradiant ${styles["raise-abr-verify-btn"]}`} onClick={config.handleVerifyMobileNumber} disabled={config.currentStep !== config.Steps.RAISE_ABR_REQUEST}>
                    Verify
                  </Button>
                </div>
              </Form.Item>

              <Form.Item {...inputLayout2} name="first_name" label="First name" required>
                <Input name="first_name" disabled={!!config?.userDetails?.first_name?.length || config.currentStep !== config.Steps.RAISE_ABR_REQUEST} />
              </Form.Item>

              <Form.Item {...inputLayout2} name="last_name" label="Last Name" required>
                <Input name="last_name" disabled={!!config?.userDetails?.last_name?.length || config.currentStep !== config.Steps.RAISE_ABR_REQUEST} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Row className={styles["margin-btm-12"]}>
                <Col span={20}>
                  <Checkbox
                    value={config.isValetParkingChecked}
                    disabled={config.currentStep !== config.Steps.RAISE_ABR_REQUEST}
                    onChange={(e) => {
                      config.handleValetParkingChecked(e.target.checked);
                    }}
                  >
                    <Text strong>Valet Parking Amount:{` ( ${config.slotDetails.valetParkingAmount} )`}</Text>
                  </Checkbox>
                </Col>
                <Col span={4} className={styles["col-text-align"]}>
                  <Text>₹ {config.isValetParkingChecked ? config.slotDetails.valetParkingAmount : 0}</Text>
                </Col>
              </Row>

              <Row className={styles["margin-btm-12"]}>
                <Col span={20}>
                  <Text strong>Parking Amount:</Text>
                </Col>
                <Col span={4} className={styles["col-text-align"]}>
                  <Text>₹ {config.slotDetails.totalAmount}</Text>
                </Col>
              </Row>

              <Row className={styles["margin-btm-12"]}>
                <Col span={20}>
                  <Text strong>Total Amount:</Text>
                </Col>
                <Col span={4} className={styles["col-text-align"]}>
                  <Text>₹ {config.slotDetails.totalAmount + (config.isValetParkingChecked ? config.slotDetails.valetParkingAmount : 0)}</Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </>
    );
  };

  const Buttons = () => {
    return (
      <div className={styles.formFooter}>
        <Form.Item className={styles.formItemFloatR20}>
          <Button disabled={config.saving} className={`gx-mb-0 button-gradiant`} onClick={config.handleCloseModal}>
            Close
          </Button>
        </Form.Item>

        {config.currentStep === config.Steps.CHECK_AVAILABLE_SLOTS && (
          <Form.Item className={styles.formItemFloatR20}>
            <Button className={`gx-mb-0 button-gradiant`} htmlType="submit" disabled={config.saving}>
              {config.saving ? <CircularProgressBar /> : "Check Slot Availability"}
            </Button>
          </Form.Item>
        )}

        {config.currentStep === config.Steps.RAISE_ABR_REQUEST && (
          <Form.Item className={styles.formItemFloatR20}>
            <Button className={`gx-mb-0 button-gradiant`} htmlType="submit" disabled={config.saving}>
              {config.saving ? <CircularProgressBar /> : "Create ABR Request"}
            </Button>
          </Form.Item>
        )}

        {config.currentStep === config.Steps.RAISE_BOOKING_REQUEST && (
          <Form.Item className={styles.formItemFloatR20}>
            <Button className={`gx-mb-0 button-gradiant`} htmlType="submit" disabled={config.saving}>
              {config.saving ? <CircularProgressBar /> : "Raise Booking Request"}
            </Button>
          </Form.Item>
        )}
      </div>
    );
  };

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`Pay & Park`}</div>}
      open={true}
      onCancel={config.handleCloseModal}
      width={"80%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
      footer={null}
    >
      {config.loading && <Loading />}

      {!config.loading && (
        <Form className={styles["form"]} {...formItemLayout} form={config.form} validateTrigger="onBlur" colon={false} name="register" onFinish={config.handleFinish}>
          {checkSlotAVailability()}
          <Divider />

          {raiseAdvanceBookingRequest()}
          <Divider />

          {config.bookingRequest && <KeyFobAllocation form={config.form} advance_booking_request_id={config.bookingRequest.id} booking_id={config.bookingRequest.booking_id} vehicle_type_id={config.bookingRequest.vehicle_type_id} />}

          <Buttons />
        </Form>
      )}
    </Modal>
  );
};

export default PayAndParkModal;
